import type { ReactNode } from "react";
import { executeStoredProcedure } from "../../api/endpoints.ts";
import type { ButtonClickInfo } from "./cellRenderer.tsx";
import type { InputParam } from "../../types/grid.ts";

/**
 * Shared "run the stored-procedure behind this grid button" fallback, used by
 * every grid host once `dispatchButtonAction` reports a button unhandled.
 *
 * Mirrors WebAdmin2's generic ButtonHandler tail: a grid button whose page name
 * is a procedure (e.g. `SP_Delete_EmailTemplate`, types 110/113/115/116/117)
 * runs that SP with the row's cells as positional params (`p0..pN`) and the
 * button type as the QID. Centralised here so both the main grid host
 * (`GridPage`) and `genericGridPage` get identical, safe behaviour:
 *
 *   • Skips non-procedure page names — legacy nav/no-op codes use `?`, `!`, ``
 *     or pure numbers; firing `executeStoredProcedure("53", …)` just errors.
 *   • Confirms before destructive actions (delete/remove/rollback, or the known
 *     destructive types 110/115) so a stray click can't silently delete a row.
 *   • Refreshes the grid on success so the mutation is reflected.
 */

export interface SpButtonDeps {
  confirm: (opts: {
    title: string;
    body?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
  }) => Promise<boolean>;
  notify: (message: string, kind: "success" | "error") => void;
  refresh?: () => void;
}

/** A button whose page name looks like a stored procedure (contains a letter),
 *  vs. a legacy numeric/`?`/`!`/empty nav code. */
export function isStoredProcedureButton(info: ButtonClickInfo): boolean {
  return /[a-z]/i.test((info.pageName ?? "").trim());
}

/**
 * Runs the SP-exec fallback for a grid button.
 *
 * Returns `true` when the button was an SP-exec button (executed, or the user
 * aborted the confirm) — i.e. it has been dealt with. Returns `false` when the
 * page name isn't a procedure, so the caller can log/ignore it.
 */
export async function runStoredProcedureButton(
  info: ButtonClickInfo,
  deps: SpButtonDeps,
): Promise<boolean> {
  if (!isStoredProcedureButton(info)) return false;
  const page = info.pageName.trim();

  const destructive = info.type === 110 || info.type === 115 || /delete|remove|rollback/i.test(info.label);
  if (destructive) {
    const ok = await deps.confirm({
      title: info.label || "Confirm",
      body: `Run “${info.label || page}”? This may not be undoable.`,
      confirmLabel: info.label || "Confirm",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return true; // handled — user aborted
  }

  try {
    const params: InputParam[] = info.rowData.map((v, i) => ({ sColumn: `p${i}`, oValue: v }));
    const result = await executeStoredProcedure(page, params, info.type);
    if (result.bError) {
      deps.notify(result.sMessage || "Operation failed", "error");
    } else {
      deps.notify(result.sMessage || "Operation completed", "success");
      deps.refresh?.();
    }
  } catch {
    deps.notify("Operation failed", "error");
  }
  return true;
}
